import {
  type CollectionDefinition,
  GenderEnum,
  MatchUpTypeEnum,
  type TieFormat,
} from '@clubspark-node/clubspark-tennis-types';
import { type IMatchCollection } from '@clubspark-react/ui/dist/components/types';
import { type TFunction } from 'i18next';
import { tournamentEngine } from 'tods-competition-factory';

import { getClientConfig } from 'config/clients';
import { defaultAgeDivisions } from 'mocks/policies/AGE_DIVISIONS';
import { Category, Gender } from 'typedefs/event';
import { type EventInterface } from 'typedefs/store/tmxTypes';
import { parsePointAwardingType } from 'utils/scorecard';

export interface HydratedCollectionDefinition extends CollectionDefinition {
  id?: string;
  isEdited?: boolean;
}

export interface HydratedTieFormat extends TieFormat {
  collectionDefinitions: HydratedCollectionDefinition[];
}

export interface AgeDivision {
  value: string;
  label: string;
}

export const genderOptions = [
  {
    id: 1,
    description: 'Male',
    gender: GenderEnum.MALE,
  },
  {
    id: 2,
    description: 'Female',
    gender: GenderEnum.FEMALE,
  },
  {
    id: 3,
    description: 'Mixed',
    gender: GenderEnum.MIXED,
  },
  {
    id: 4,
    description: 'Coed',
    gender: GenderEnum.ANY,
  },
];

export const getDefaultGenderOptions = (eventGender: Gender | GenderEnum, matchUpType: MatchUpTypeEnum) => {
  const isMensEvent = eventGender === Gender.BOYS || eventGender === Gender.MALE || eventGender === Gender.MEN;
  const isWomensEvent = eventGender === Gender.FEMALE || eventGender === Gender.GIRLS || eventGender === Gender.WOMEN;
  const isMixedSingles = eventGender === Gender.MIXED && matchUpType === MatchUpTypeEnum.SINGLES;
  const isMixedDoubles = eventGender === Gender.MIXED && matchUpType === MatchUpTypeEnum.DOUBLES;
  const isCoedSingles = eventGender === GenderEnum.ANY && matchUpType === MatchUpTypeEnum.SINGLES;
  const isCoedDoubles = eventGender === GenderEnum.ANY && matchUpType === MatchUpTypeEnum.DOUBLES;

  return [
    (!matchUpType || isMensEvent || isMixedSingles || isMixedDoubles || isCoedSingles || isCoedDoubles) && {
      id: 1,
      description: 'Male',
      gender: GenderEnum.MALE,
    },
    (!matchUpType || isWomensEvent || isMixedSingles || isMixedDoubles || isCoedSingles || isCoedDoubles) && {
      id: 2,
      description: 'Female',
      gender: GenderEnum.FEMALE,
    },
    (!matchUpType || isMixedDoubles || isCoedSingles || isCoedDoubles) &&
      matchUpType !== MatchUpTypeEnum.SINGLES && {
        id: 3,
        description: 'Mixed',
        gender: GenderEnum.MIXED,
      },
    (!matchUpType || isCoedSingles || isCoedDoubles) && {
      id: 4,
      description: 'Coed',
      gender: GenderEnum.ANY,
    },
  ].filter(Boolean);
};

export const getDefaultCollectionGender = (eventGender: Gender | GenderEnum, matchUpType: MatchUpTypeEnum) => {
  if (matchUpType === MatchUpTypeEnum.SINGLES || matchUpType === MatchUpTypeEnum.DOUBLES) {
    if (eventGender === Gender.BOYS || eventGender === Gender.MALE || eventGender === Gender.MEN)
      return GenderEnum.MALE;
    if (eventGender === Gender.GIRLS || eventGender === Gender.FEMALE || eventGender === Gender.WOMEN)
      return GenderEnum.FEMALE;
    if (eventGender === Gender.MIXED) return GenderEnum.MIXED;
    if (eventGender === GenderEnum.ANY) return GenderEnum.ANY;
  }

  return undefined;
};

export const mapAdultAgeDivision = (ageDivision: string) => {
  const age = Number.parseInt(ageDivision?.split('O')?.[1]);
  return {
    value: ageDivision,
    label: !Number.isNaN(age) ? `${age}+` : 'Open',
  };
};

export const mapJuniorAgeDivision = (ageDivision: string) => {
  const age = Number.parseInt(ageDivision?.split('U')?.[1]);
  return {
    value: ageDivision,
    label: `${age}U`,
  };
};

export const mapWheelchairAgeDivision = (ageDivision: string) => ({ value: ageDivision, label: ageDivision });

export const getAgeDivisionOptions = (
  eventCategoryCode: string,
  tournamentCategory: Category,
  isJuniorCircuit?: boolean,
): Array<AgeDivision> => {
  const ageDivisions = getClientConfig().ageDivisions[tournamentCategory];

  if (tournamentCategory === Category.WHEELCHAIR) return ageDivisions.map(mapWheelchairAgeDivision);
  if (tournamentCategory === Category.ADULT && eventCategoryCode === 'OPEN')
    return ageDivisions.map((ageDivision) => mapAdultAgeDivision(ageDivision));
  if (tournamentCategory === Category.ADULT)
    return ageDivisions
      ?.filter((division) => {
        const age = Number.parseInt(division.split('O')?.[1]);
        const eventAge = Number.parseInt(eventCategoryCode.split('O')?.[1]);
        return !Number.isNaN(age) && !Number.isNaN(eventAge) && age >= eventAge;
      })
      .map((ageDivision) => mapAdultAgeDivision(ageDivision));

  if (tournamentCategory === Category.JUNIOR)
    return ageDivisions
      ?.filter((division) => {
        const age = Number.parseInt(division.split('U')?.[1]);
        const eventAge = Number.parseInt(eventCategoryCode.split('U')?.[1]);
        if (!isJuniorCircuit && age <= 10) return false;
        return !Number.isNaN(age) && !Number.isNaN(eventAge) && age <= eventAge;
      })
      .map((ageDivision) => mapJuniorAgeDivision(ageDivision));

  return defaultAgeDivisions;
};

export const getMatchCollectionsTableData = (
  collectionDefinitions: HydratedCollectionDefinition[],
  tournamentPolicies,
  scorecardTemplateErrors: Record<string, any>,
  t: TFunction,
): IMatchCollection[] => {
  return collectionDefinitions.map((collection) => ({
    ageDivision: getCorrectAgeCategory(collection?.category?.ageCategoryCode)?.ageCategoryCodeDisplay,
    ageError: scorecardTemplateErrors?.[collection.collectionId]?.age ? t('invalidAgeText') : undefined,
    collectionName: collection.collectionName,
    collectionValue: collection.collectionValue,
    collectionId: collection.collectionId,
    collectionOrder: collection.collectionOrder,
    gender: genderOptions?.find((option) => option.gender === collection.gender)?.description,
    genderError: scorecardTemplateErrors?.[collection.collectionId]?.gender ? t('invalidGenderText') : undefined,
    matchUpCount: collection.matchUpCount,
    matchUpType: collection.matchUpType.charAt(0) + collection.matchUpType.slice(1).toLowerCase(),
    pointAwardingType: parsePointAwardingType(collection),
    scoreFormatDescription: tournamentPolicies?.scoring?.matchUpFormats?.find(
      (policyMatchUpFormat) => policyMatchUpFormat?.matchUpFormat === collection.matchUpFormat,
    )?.description,
  }));
};

export const getSaveScorecardScopeParams = ({
  matchUpId,
  structureId,
  drawId,
}: {
  matchUpId?: string;
  structureId?: string;
  drawId: string;
}) => {
  if (matchUpId) return { matchUpId, drawId };
  if (structureId) return { structureId, drawId };
  return { drawId };
};

export const getExistingPointAwardingType = (selectedMatchCollection) => {
  return selectedMatchCollection
    ? Object.entries(selectedMatchCollection)
        .map(([key, value]) => {
          if (['matchUpValue', 'collectionValue', 'scoreValue', 'setValue'].includes(key) && value !== undefined) {
            return { [key]: value };
          }
          return undefined;
        })
        .filter(Boolean)?.[0]
    : undefined;
};

export const getIsAdultFromAgeCategory = (categoryCode?: string) =>
  categoryCode?.includes('+') || categoryCode?.includes('O');

export const getIsWheelchairFromAgeCategory = (categoryCode?: string) =>
  categoryCode === 'A' || categoryCode === 'B' || categoryCode === 'C' || categoryCode === 'D';

export const getCorrectAgeCategory = (categoryCode: string) => {
  const isAdult = getIsAdultFromAgeCategory(categoryCode);
  const isWheelchair = getIsWheelchairFromAgeCategory(categoryCode);
  const isOpen = categoryCode?.toLowerCase() === 'open';
  let splitter = isAdult ? '+' : 'U';
  if (categoryCode?.includes('O')) splitter = 'O';
  const splittedCategoryCode = categoryCode?.split(splitter);
  let age = Number.parseInt(splittedCategoryCode?.[0]);
  if (isNaN(age)) age = Number.parseInt(splittedCategoryCode?.[1]);

  if (isOpen)
    return {
      age: -1,
      ageCategoryCode: 'Open',
      ageCategoryCodeDisplay: 'Open',
    };

  if (isNaN(age) || isWheelchair)
    return {
      age: undefined,
      ageCategoryCode: 'Open',
      ageCategoryCodeDisplay: isWheelchair ? categoryCode : '',
    };

  return {
    age: age,
    ageCategoryCode: `${isAdult ? 'O' : 'U'}${age}`,
    ageCategoryCodeDisplay: `${age}${isAdult ? '+' : 'U'}`,
  };
};

export const validateScorecardTemplate = (
  collectionDefinitions: CollectionDefinition[],
  event: EventInterface,
  isDualMatch?: boolean,
) => {
  if (collectionDefinitions?.length) {
    if (event?.category?.subType === Category.WHEELCHAIR || isDualMatch) return {};

    return collectionDefinitions.reduce((acc: Record<string, any>, current: any) => {
      const collectionDefinitionErrors = { gender: undefined, age: undefined };

      const genderResult = tournamentEngine.tieFormatGenderValidityCheck({
        referenceEvent: event,
        referenceGender: event?.gender,
        gender: current?.gender,
        matchUpType: current?.matchUpType,
      });
      if (genderResult.error) {
        collectionDefinitionErrors.gender = genderResult?.error;
      }

      const { age, ageCategoryCode } = getCorrectAgeCategory(current?.category?.ageCategoryCode);
      const eventAge = getCorrectAgeCategory(event?.category?.ageCategoryCode)?.age;
      const isAdult = getIsAdultFromAgeCategory(current?.category?.ageCategoryCode);
      const isEventAdult = getIsAdultFromAgeCategory(event?.category?.ageCategoryCode);

      if (
        (current?.category?.ageCategoryCode?.toLowerCase() === 'open' && event?.category?.ageCategoryCode !== 'OPEN') ||
        (event?.category?.ageCategoryCode?.toLocaleLowerCase() === 'open' && !isAdult)
      )
        return { ...acc, [current.collectionId]: { ...collectionDefinitionErrors, age: true } };

      const ageResult = tournamentEngine.categoryCanContain({
        childCategory: {
          ...event?.category,
          ageCategoryCode,
          ageMin: isAdult ? age + 1 : undefined,
          ageMax: !isAdult ? age : undefined,
        },
        category: {
          ...event?.category,
          ageMin: isEventAdult ? eventAge + 1 : undefined,
          ageMax: !isEventAdult ? eventAge : undefined,
        },
        withDetails: true,
      });
      if (ageResult?.invalidAgeMin || ageResult?.invalidAgeMax) {
        collectionDefinitionErrors.age = ageResult?.childCategoryDetails?.errors ?? true;
      }

      if (collectionDefinitionErrors?.age || collectionDefinitionErrors?.gender)
        return { ...acc, [current.collectionId]: collectionDefinitionErrors };

      return acc;
    }, {});
  }

  return {};
};
